import React, { createContext, useContext, useState } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  ShieldCheck,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { MainStore } from "../../stores/Main";
import useModal from "hooks/use-modal";
import API from "../../utils/api";
import { trackChatLinkClicked } from "../../utils/posthogTracking";

// Persistent Intercom-style chat widget. The panel and its iframe are mounted
// ONCE at the app root (above the router Switch) and survive every in-app
// navigation — that is the entire point: the chat's websocket lives inside the
// iframe, and an iframe that never unmounts never drops the conversation.
//
// Invariants (violating any of these silently kills live chats):
//  - ONE iframe element, ever. The SSO token in chatUrl is single-use, so a
//    remounted iframe re-auths with a spent token and dead-ends. Desktop panel
//    and mobile full-screen are the same DOM node restyled by responsive
//    classes — never two branches or a media-query swap.
//  - Once the iframe exists, hide with styles only. `return null` is allowed
//    only before the first chatUrl (nothing to lose yet).
//  - No key above the iframe may depend on open/content state.
//  - Motion owns opacity/y (tailwind.config sets `important: true`, so
//    opacity-0/translate-y-* utilities would override Motion's inline styles);
//    visibility/pointer-events are toggled separately.

const ChatWidgetContext = createContext({
  isOpen: false,
  openChat: () => {},
  closeChat: () => {},
  toggleChat: () => {},
});

export const useChatWidget = () => useContext(ChatWidgetContext);

export const ChatWidgetProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // openChat is idempotent (never a toggle): StrictMode double-invokes the
  // /dashboard/chat redirect's effect in dev.
  const value = {
    isOpen,
    openChat: () => setIsOpen(true),
    closeChat: () => setIsOpen(false),
    toggleChat: () => setIsOpen((v) => !v),
  };

  return (
    <ChatWidgetContext.Provider value={value}>
      {children}
    </ChatWidgetContext.Provider>
  );
};

const ChatWidget = () => {
  const { isOpen, closeChat, toggleChat } = useChatWidget();
  const { isOpen: modalOpen } = useModal();
  const { isAuthenticated, acceptedDisclosures, agencyDetails } =
    useContext(MainStore);

  const [relationship, setRelationship] = useState(null);
  const [chatUrl, setChatUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const eligible =
    isAuthenticated && acceptedDisclosures && agencyDetails?.base_chat_domain;

  const startSelf = async () => {
    setRelationship("Self");
    setLoading(true);
    setError("");
    try {
      trackChatLinkClicked("chat_widget", "Self");
      const response = await API.post("/chat/sso-token", {
        relationship: "Self",
      });
      if (response?.data?.url) {
        setChatUrl(response.data.url);
      } else {
        setError("We couldn't start your chat session. Please try again.");
        setRelationship(null);
      }
    } catch (e) {
      setError("We couldn't start your chat session. Please try again.");
      setRelationship(null);
    }
    setLoading(false);
  };

  // Only safe to bail out entirely before the iframe exists; afterwards the
  // widget must stay mounted (styles-only hiding) to keep the socket alive.
  if (!chatUrl && !eligible) {
    return null;
  }

  const visible = isOpen && !modalOpen;

  return (
    <>
      {/* Panel — one node for desktop (floating card) and mobile (full
          screen), restyled responsively. Always mounted; Motion animates
          opacity/y, `invisible` yanks it instantly while a modal is up. */}
      <motion.div
        initial={false}
        animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{ pointerEvents: visible ? "auto" : "none" }}
        className={`fixed z-50 inset-0 flex flex-col overflow-hidden bg-gray-900 lg:inset-auto lg:bottom-24 lg:right-6 lg:w-[400px] lg:h-[min(640px,calc(100vh-8rem))] lg:rounded-2xl lg:border lg:border-white/10 lg:shadow-2xl ${
          modalOpen ? "invisible" : ""
        }`}
        aria-hidden={!visible}
      >
        {/* Mobile top bar: the floating launcher would overlap the chat
            composer, so close lives here below lg. */}
        <div className="lg:hidden flex items-center justify-between h-14 px-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2 text-white">
            <MessageCircle className="w-5 h-5 text-blue-400" />
            <span className="font-semibold">Chat</span>
          </div>
          <button
            type="button"
            onClick={closeChat}
            className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0">
          {chatUrl ? (
            <iframe
              src={chatUrl}
              title="Freede Chat"
              className="w-full h-full border-0 bg-gray-900"
            />
          ) : (
            <div className="h-full overflow-y-auto p-6">
              {relationship !== "Other" && (
                <>
                  <div className="flex items-center gap-2 text-white/80 mb-2">
                    <ShieldCheck className="w-5 h-5 text-blue-400" />
                    <span className="font-semibold">Before we begin</span>
                  </div>
                  <p className="text-white/70 mb-5">
                    What is your relationship to this account?
                  </p>
                  {error && (
                    <p className="text-red-400 text-sm mb-4">{error}</p>
                  )}
                  <div className="grid gap-3">
                    <button
                      type="button"
                      onClick={startSelf}
                      disabled={loading}
                      className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-white/20 transition-all text-left disabled:opacity-60"
                    >
                      <UserCheck className="w-6 h-6 text-white/70 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-1">
                          I am the account holder
                        </h3>
                        <p className="text-white/60 text-sm">
                          {loading
                            ? "Starting your secure session…"
                            : "Start a pre-authenticated chat — no second login."}
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRelationship("Other")}
                      disabled={loading}
                      className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-white/20 transition-all text-left disabled:opacity-60"
                    >
                      <Users className="w-6 h-6 text-white/70 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-1">
                          I'm contacting on someone's behalf
                        </h3>
                        <p className="text-white/60 text-sm">
                          A family member, representative, or other third
                          party.
                        </p>
                      </div>
                    </button>
                  </div>
                </>
              )}

              {relationship === "Other" && (
                <div className="text-center pt-8">
                  <Users className="w-10 h-10 text-white/40 mx-auto mb-4" />
                  <h3 className="text-white font-semibold text-lg mb-2">
                    We don't support third-party communications
                  </h3>
                  <p className="text-white/60 mb-6">
                    For privacy and compliance, we can only chat with the
                    account holder at this time. Please ask them to sign in
                    and chat directly.
                  </p>
                  <button
                    type="button"
                    onClick={() => setRelationship(null)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium transition-all"
                  >
                    Go back
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Launcher bubble. Hidden below lg while the panel is open (the panel
          is full-screen there and has its own close). */}
      <button
        type="button"
        onClick={toggleChat}
        className={`fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-900/40 items-center justify-center text-white hover:scale-105 transition-transform ${
          isOpen ? "hidden lg:flex" : "flex"
        } ${modalOpen ? "invisible" : ""}`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </button>
    </>
  );
};

export default ChatWidget;
